import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { Storage } from 'vscode/vscode/vs/base/parts/storage/common/storage';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import { AbstractStorageService } from 'vscode/vscode/vs/platform/storage/common/storage';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { BrowserStorageService } from './vscode/src/vs/workbench/services/storage/browser/storageService.js';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { IUserDataProfileService } from 'vscode/vscode/vs/workbench/services/userDataProfile/common/userDataProfile.service';
import { IHostService } from 'vscode/vscode/vs/workbench/services/host/browser/host.service';
import { ExtensionStorageService } from 'vscode/vscode/vs/platform/extensionManagement/common/extensionStorage';
import { IExtensionStorageService } from 'vscode/vscode/vs/platform/extensionManagement/common/extensionStorage.service';
import { registerServiceInitializePreParticipant } from 'vscode/lifecycle';
import { getWorkspaceIdentifier } from 'vscode/workbench';

var StorageScope;
( (function(StorageScope) {
    StorageScope[StorageScope["APPLICATION"] = -1] = "APPLICATION";
    StorageScope[StorageScope["PROFILE"] = 0] = "PROFILE";
    StorageScope[StorageScope["WORKSPACE"] = 1] = "WORKSPACE";
})(StorageScope || (StorageScope = {})));
class ExternalStorage extends Storage {
    constructor(scope, provider) {
        const items = provider.read(scope);
        super(new ExternalStorageDatabase(scope, provider, items));
        if (items != null) {
            for (const [key, value] of items) {
                this.items.set(key, value);
            }
        }
    }
}
class ExternalStorageDatabase {
    constructor(scope, provider, items = new Map()) {
        this.scope = scope;
        this.provider = provider;
        this.items = items;
        this.onDidChangeItemsExternal = this.provider.onDidChange ?? Event.None;
    }
    async getItems() {
        return this.items;
    }
    async updateItems(request) {
        request.insert?.forEach((value, key) => this.items.set(key, value));
        request.delete?.forEach(key => this.items.delete(key));
        await this.provider.write(this.scope, this.items);
    }
    async close() {
        return this.provider.close?.(this.scope);
    }
    async optimize() {
        return this.provider.optimize?.(this.scope);
    }
}
class ExternalStorageService extends AbstractStorageService {
    constructor(provider, fallbackOverride) {
        super({
            flushInterval: 5000
        });
        this.provider = provider;
        this.fallbackOverride = fallbackOverride;
        this.applicationStorage = this._register(new ExternalStorage(StorageScope.APPLICATION, this.provider));
        this.profileStorage = this._register(new ExternalStorage(StorageScope.PROFILE, this.provider));
        this.workspaceStorage = this._register(new ExternalStorage(StorageScope.WORKSPACE, this.provider));
        this._register(this.workspaceStorage.onDidChangeStorage(key => this.emitDidChangeValue(1 , key)));
        this._register(this.profileStorage.onDidChangeStorage(key => this.emitDidChangeValue(0 , key)));
        this._register(this.applicationStorage.onDidChangeStorage(key => this.emitDidChangeValue(-1 , key)));
    }
    getStorage(scope) {
        switch (scope) {
            case -1 :
                return this.applicationStorage;
            case 0 :
                return this.profileStorage;
            default:
                return this.workspaceStorage;
        }
    }
    getLogDetails(scope) {
        switch (scope) {
            case -1 :
                return 'External (application)';
            case 0 :
                return 'External (profile)';
            default:
                return 'External (workspace)';
        }
    }
    async doInitialize() {
    }
    async switchToProfile() {
    }
    async switchToWorkspace() {
    }
    hasScope(_scope) {
        return false;
    }
    get(key, scope, fallbackValue) {
        return this.getStorage(scope).get(key, this.fallbackOverride?.[key] ?? fallbackValue);
    }
    getBoolean(key, scope, fallbackValue) {
        return this.getStorage(scope).getBoolean(key, this.fallbackOverride?.[key] ?? fallbackValue);
    }
    getNumber(key, scope, fallbackValue) {
        return this.getStorage(scope).getNumber(key, this.fallbackOverride?.[key] ?? fallbackValue);
    }
    getObject(key, scope, fallbackValue) {
        return this.getStorage(scope).getObject(key, this.fallbackOverride?.[key] ?? fallbackValue);
    }
}
registerServiceInitializePreParticipant(async (accessor) => {
    const storageService = accessor.get(IStorageService);
    const hostService = accessor.get(IHostService);
    if (storageService instanceof AbstractStorageService) {
        await storageService.initialize();
        hostService.onDidChangeFocus(focus => {
            if (!focus) {
                void storageService.flush();
            }
        });
    }
});
let InjectedBrowserStorageService = class InjectedBrowserStorageService extends BrowserStorageService {
    constructor(fallbackOverride, userDataProfileService, logService) {
        super(getWorkspaceIdentifier(), userDataProfileService, logService);
        this.fallbackOverride = fallbackOverride;
    }
    get(key, scope, fallbackValue) {
        return this.getStorage(scope)?.get(key, this.fallbackOverride?.[key] ?? fallbackValue);
    }
    getBoolean(key, scope, fallbackValue) {
        return this.getStorage(scope)?.getBoolean(key, this.fallbackOverride?.[key] ?? fallbackValue);
    }
    getNumber(key, scope, fallbackValue) {
        return this.getStorage(scope)?.getNumber(key, this.fallbackOverride?.[key] ?? fallbackValue);
    }
    getObject(key, scope, fallbackValue) {
        return this.getStorage(scope)?.getObject(key, this.fallbackOverride?.[key] ?? fallbackValue);
    }
};
InjectedBrowserStorageService = __decorate([
    ( __param(1, IUserDataProfileService)),
    ( __param(2, ILogService))
], InjectedBrowserStorageService);
function getStorageServiceOverride({ customProvider, fallbackOverride } = {}) {
    if (customProvider != null) {
        return {
            [( IStorageService.toString())]: new SyncDescriptor(ExternalStorageService, [customProvider, fallbackOverride], true),
            [( IExtensionStorageService.toString())]: new SyncDescriptor(ExtensionStorageService, [], true)
        };
    }
    else {
        return {
            [( IStorageService.toString())]: new SyncDescriptor(InjectedBrowserStorageService, [fallbackOverride], true),
            [( IExtensionStorageService.toString())]: new SyncDescriptor(ExtensionStorageService, [], true)
        };
    }
}

export { InjectedBrowserStorageService as BrowserStorageService, ExternalStorageService, StorageScope, getStorageServiceOverride as default };
